import React from 'react';
import { Box, ButtonBase, Chip, Typography, type SxProps } from '@mui/material';

interface SubGroupSectionProps {
  icon: React.ComponentType<{ sx?: SxProps }>;
  name: string;
  count: number;
  onTextClick?: () => void;
  secondaryText?: React.ReactNode;
  children: React.ReactNode;
}

export const SubGroupSection = React.memo(function SubGroupSection({
  icon: Icon,
  name,
  count,
  onTextClick,
  secondaryText,
  children,
}: SubGroupSectionProps) {
  return (
    <Box data-testid="sub-group-section">
      {/* Sub-group header row — full-width hover */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          width: '100%',
          py: 1,
          pl: { xs: 3.5, sm: 4 },
          pr: { xs: 1.5, sm: 2 },
          minHeight: 40,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {onTextClick ? (
          <ButtonBase
            onClick={onTextClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              '&:hover .subgroup-label': { color: 'primary.main' },
            }}
          >
            <Icon
              sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }}
            />
            <Typography
              className="subgroup-label"
              variant="body1"
              sx={{
                fontWeight: 500,
                transition: 'color 150ms ease',
              }}
            >
              {name}
            </Typography>
          </ButtonBase>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon
              sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }}
            />
            <Typography
              className="subgroup-label"
              variant="body1"
              sx={{
                fontWeight: 500,
                transition: 'color 150ms ease',
              }}
            >
              {name}
            </Typography>
          </Box>
        )}
        {count > 1 && (
          <Chip
            label={count}
            size="small"
            variant="outlined"
            sx={{
              height: 20,
              typography: 'caption',
              fontWeight: 500,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}
        {secondaryText && (
          <>
            <Box sx={{ flex: 1 }} />
            {secondaryText}
          </>
        )}
      </Box>

      {/* Children — always visible */}
      {children}
    </Box>
  );
});
